
import { Users, Bot, DollarSign, FileText, Settings, Home, LayoutDashboard, UserCog, User, Phone, ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useRole } from "@/contexts/RoleContext";
import { useState, useMemo } from "react";
import logoImage from "@/assets/design/logo.png";
import { useAuth } from "@/components/AuthProvider";
import { useClientBotType } from "@/hooks/useClientBotType";

interface SidebarProps {
  className?: string;
  collapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
}

interface SidebarItemProps {
  icon: React.ElementType;
  label: string;
  href: string;
  active?: boolean;
  collapsed?: boolean;
}

const SidebarItem = ({ icon: Icon, label, href, active, collapsed }: SidebarItemProps) => {
  return (
    <Link
      to={href}
      className={cn(
        "group relative flex items-center rounded-figma-button text-sm font-medium transition-all duration-300 hover:bg-white/10 hover:backdrop-blur-sm",
        collapsed ? "justify-center p-3" : "gap-4 px-4 py-3",
        active
          ? "bg-white text-figma-primary shadow-figma backdrop-blur-sm"
          : "text-white/90 hover:text-white"
      )}
      title={collapsed ? label : undefined}
    >
      <Icon className={cn(
        "h-5 w-5 transition-all duration-300 flex-shrink-0",
        active ? "text-figma-primary" : "text-white/70 group-hover:text-white"
      )} />
      {!collapsed && (
        <span className="font-medium tracking-wide">{label}</span>
      )}
      {active && !collapsed && (
        <div className="absolute right-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-l-full bg-figma-primary" />
      )}
      {active && collapsed && (
        <div className="absolute right-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-l-full bg-white" />
      )}
    </Link>
  );
};

const iconMap: Record<string, React.ElementType> = {
  'LayoutDashboard': LayoutDashboard,
  'Users': Users,
  'Bot': Bot,
  'Phone': Phone,
  'DollarSign': DollarSign,
  'FileText': FileText,
  'Settings': Settings,
  'Home': Home,
  'UserCog': UserCog,
  'User': User
};

export function Sidebar({ className, collapsed: controlledCollapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const { availableRoutes, currentRole } = useRole();
  const { user, logout } = useAuth();
  const { botType, isLoading: botTypeLoading } = useClientBotType();
  const [internalCollapsed, setInternalCollapsed] = useState(false);

  const collapsed = controlledCollapsed ?? internalCollapsed;
  const toggleCollapse = () => {
    if (onToggleCollapse) {
      onToggleCollapse(!collapsed);
    } else {
      setInternalCollapsed(!collapsed);
    }
  };

  const handleSignOut = async () => {
    await logout();
  };

  const getUserInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'developer':
        return 'Desarrollador';
      case 'client':
        return 'Cliente';
      default:
        return 'Usuario';
    }
  };

  // Filter routes based on bot type for CLIENT role
  const filteredRoutes = useMemo(() => {
    if (currentRole !== 'client') {
      return availableRoutes;
    }

    // While loading bot type, show all routes to avoid flickering
    if (botTypeLoading) {
      return availableRoutes;
    }

    // For client role, filter routes based on bot type
    return availableRoutes.filter(route => {
      if (route.path === '/leads') {
        return botType === 'chatbot';
      }
      if (route.path === '/traces') {
        return botType === 'voicebot';
      }
      return true; // Keep all other routes
    });
  }, [availableRoutes, currentRole, botType, botTypeLoading]);

  return (
    <nav 
      aria-label="Navegación principal"
      className={cn(
        "relative flex h-screen flex-col bg-figma-primary text-white transition-all duration-300 z-30",
        collapsed ? "w-20" : "w-72",
        className
      )}>

      {/* Header */}
      <div className="relative z-10 border-b border-white/10 p-6">
        <div className={cn(
          "flex items-center mb-4",
          collapsed ? "justify-center" : "gap-3"
        )}>
          <div className="relative">
            <img 
              src={logoImage} 
              alt="TendencIA Logo" 
              className={cn(
                "object-contain",
                collapsed ? "w-10 h-10" : "w-12 h-12"
              )}
            />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold tracking-tight text-white truncate">
                TendencIA
              </h2>
              <p className="text-sm text-white/70 font-medium">
                Sistema de Control
              </p>
            </div>
          )}
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={toggleCollapse}
          className={cn(
            "flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200",
            collapsed ? "mx-auto" : "ml-auto"
          )}
          aria-label={collapsed ? "Expandir menú lateral" : "Colapsar menú lateral"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expandir sidebar" : "Colapsar sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-white" />
          ) : (
            <ChevronLeft className="h-4 w-4 text-white" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <div className={cn(
        "relative z-10 flex-1 py-6",
        collapsed ? "px-2" : "px-6"
      )}>
        <ul className="space-y-2" role="list">
          {filteredRoutes.map((route) => {
            const Icon = iconMap[route.icon] || Home;
            return (
              <SidebarItem
                key={route.path}
                icon={Icon}
                label={route.name}
                href={route.path}
                active={location.pathname === route.path}
                collapsed={collapsed}
              />
            );
          })}
        </ul>
      </div>

      {/* Footer */}
      <div className={cn(
        "relative z-10 border-t border-white/10 space-y-3",
        collapsed ? "p-3" : "p-6"
      )}>
        {/* Logout Button */}
        <button
          onClick={handleSignOut}
          className={cn(
            "w-full flex items-center rounded-figma-button bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-white p-3 transition-colors",
            collapsed ? "justify-center" : "gap-3"
          )}
          title="Cerrar sesión"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && (
            <span className="text-sm font-medium">Cerrar sesión</span>
          )}
        </button>

        {/* User Info (Non-clickable) */}
        <div className={cn(
          "w-full flex items-center rounded-figma-button bg-white/5 backdrop-blur-sm p-3 cursor-default",
          collapsed ? "justify-center" : "gap-3"
        )}>
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name || 'Usuario'}
              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
              onError={(e) => {
                // Fallback to initials if image fails to load
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"
            style={{ display: user?.avatarUrl ? 'none' : 'flex' }}
          >
            <span className="text-sm font-semibold text-white">
              {getUserInitials(user?.name)}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-white truncate">
                {user?.name || 'Usuario'}
              </p>
              <p className="text-xs text-white/60">
                {getRoleDisplay(user?.role || 'client')}
              </p>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
